/*
 * XNGAGE CONFIDENTIAL
 * __________________________
 *
 * Copyright (C) 2022 Xngage - All Rights Reserved
 *
 * All code or information contained herein is, and remains the
 * property of Xngage LLC and its customers. The intellectual
 * and technical concepts contained are proprietary to Xngage LLC
 * and may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Xngage LLC.
 */

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { createWidgetElement } from "@insite/client-framework/Components/ContentItemStore";
import { getPageStateByType } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getWidgetsByPageId } from "@insite/client-framework/Store/Data/Widgets/WidgetSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Button from "@insite/mobius/Button";
import Drawer, { DrawerPresentationProps } from "@insite/mobius/Drawer";
import getColor from "@insite/mobius/utilities/getColor";
import { CustomAppState } from "@stoneage/App/Store/Reducers";
import { ByActiveFilter } from "@stoneage/App/Widgets/ProductInformation/ProductInformationByActiveFilter";
import { ApplicationFilter } from "@stoneage/App/Widgets/ProductInformation/ProductInformationByApplicationFilter";
import { ProductLineFilter } from "@stoneage/App/Widgets/ProductInformation/ProductInformationByProductLineFIlter";
import { ByPurchaseFilter } from "@stoneage/App/Widgets/ProductInformation/ProductInformationByPurchaseFilter";
import { DocumentTypeFilter } from "@stoneage/App/Widgets/ProductInformation/ProductInformationByTypeFilter";
import React from "react";
import { connect, useDispatch } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: CustomAppState) => {
    const productInformationId = getPageStateByType(state, "ProductInformationPage")!.id || "";
    return {
        isDrawerOpen: state.xProductInfo.openDrawer,
        productInformationByTypeFilterTitle: getWidgetsByPageId(state, productInformationId).find(
            (widget: WidgetProps) => {
                return widget.type === "ProductInformation/ProductInformationByTypeFilter";
            },
        )?.fields.title,
        productInformationByPurchaseFilterTitle: getWidgetsByPageId(state, productInformationId).find(
            (widget: WidgetProps) => {
                return widget.type === "ProductInformation/ProductInformationByPurchaseFilter";
            },
        )?.fields.title,
        productInformationByApplicationFilterTitle: getWidgetsByPageId(state, productInformationId).find(
            (widget: WidgetProps) => {
                return widget.type === "ProductInformation/ProductInformationByApplicationFilter";
            },
        )?.fields.title,
        productInformationByProductLineFIlterTitle: getWidgetsByPageId(state, productInformationId).find(
            (widget: WidgetProps) => {
                return widget.type === "ProductInformation/ProductInformationByProductLineFIlter";
            },
        )?.fields.title,
        productInformationByActiveFilterTitle: getWidgetsByPageId(state, productInformationId).find(
            (widget: WidgetProps) => {
                return widget.type === "ProductInformation/ProductInformationByActiveFilter";
            },
        )?.fields.title,
        productInformationSearchQueryFields: getWidgetsByPageId(state, productInformationId).find(
            (widget: WidgetProps) => {
                return widget.type === "ProductInformation/ProductInformationSearchQuery";
            },
        )?.fields,
    };
};

export interface ProductInformationFiltersDrawerStyles {
    drawer?: DrawerPresentationProps;
}

const styles: ProductInformationFiltersDrawerStyles = {
    drawer: {
        position: "left",
        size: 300,
        closeButtonProps: {
            shape: "pill",
            buttonType: "solid",
            color: "common.background",
            size: 40,
        },
        cssOverrides: {
            drawerTitle: css`
                background: ${getColor("common.background")};
                padding: 0 16px;
            `,
            drawerBody: css`
                background: ${getColor("common.background")};
            `,
            drawerContent: css`
                padding: 0 16px;
            `,
            headlineTypography: css`
                margin: 0;
                color: ${getColor("common.black")};
            `,
        },
        headlineTypographyProps: {
            color: "text.main",
        },
    },
};

type Props = ReturnType<typeof mapStateToProps>;

const FiltersDrawer = ({
    isDrawerOpen,
    productInformationByTypeFilterTitle,
    productInformationByPurchaseFilterTitle,
    productInformationByApplicationFilterTitle,
    productInformationByProductLineFIlterTitle,
    productInformationByActiveFilterTitle,
    productInformationSearchQueryFields,
}: Props) => {
    const dispatch = useDispatch();

    const drawerCloseHandler = () => {
        dispatch({
            type: "Pages/ProductInformation/ToggleFilteresDrawer",
            isOpen: false,
        });
    };

    return (
        <Drawer
            {...styles.drawer}
            isOpen={isDrawerOpen}
            headline={translate("Filters")}
            handleClose={drawerCloseHandler}
        >
            {createWidgetElement("ProductInformation/ProductInformationSearchQuery", {
                fields: productInformationSearchQueryFields || {},
            })}
            <ByPurchaseFilter fields={{ title: productInformationByPurchaseFilterTitle }} />
            <ByActiveFilter fields={{ title: productInformationByActiveFilterTitle }} />
            <DocumentTypeFilter fields={{ title: productInformationByTypeFilterTitle }} />
            <ApplicationFilter fields={{ title: productInformationByApplicationFilterTitle }} />
            <ProductLineFilter fields={{ title: productInformationByProductLineFIlterTitle }} />
            <StyledWrapper
                css={css`
                    margin: 15px 0;
                    display: flex;
                    justify-content: center;
                `}
            >
                <Button
                    onClick={drawerCloseHandler}
                    variant="secondary"
                    css={css`
                        width: 100%;
                        justify-content: center;
                    `}
                    {...({ showIcon: false } as any)}
                >
                    {translate("Close Filter")}
                </Button>
            </StyledWrapper>
        </Drawer>
    );
};

export default connect(mapStateToProps)(FiltersDrawer);
